/**
 * Dev/test-only impersonation API client (v2 dev endpoints).
 *
 * Lets a developer sign in AS an existing member to see what that member sees
 * (my-account, orders, subscriptions). The backend mints the regular member
 * session cookie for the target member and hard-refuses in Production; the
 * widget is additionally hidden unless this is a Vite dev-server build or
 * ENABLE_IMPERSONATION is set in the environment's env-config.json ConfigMap.
 *
 * On deployed environments (test) the backend also requires the caller's own
 * session to be a Natlink staff login — see isImpersonationAllowedForSession.
 */

import { getEnv } from "@/config/env";
import { postJsonWithProblemDetails } from "@/api/http";
import { isNatlinkStaffEmail } from "@/utils/impersonation";
import type { Result } from "@/types/layout";

export interface ImpersonationViewModel {
  userEmail: string | null;
  userName: string | null;
  userType: string | null;
}

/**
 * Whether the dev impersonation widget should render at all.
 *
 * import.meta.env.DEV covers local `npm run dev` and is compiled to `false`
 * in production builds, so the flag deliberately does NOT live in the bundled
 * public/config/env-config.json — that file ships in every Docker image and
 * is what gets served when a deployed environment has no ConfigMap mounted
 * over it, so it must never enable the widget.
 */
export function isImpersonationEnabled(): boolean {
  return import.meta.env.DEV || getEnv().enableImpersonation;
}

/**
 * Whether the *current* session may impersonate, mirroring the backend guard.
 *
 * A developer machine trusts whoever is at the keyboard; every deployed
 * environment where the widget is switched on (test only) requires an active
 * Natlink staff login. Because impersonating replaces that session with the
 * target member's, one Natlink login yields one impersonation — switching to
 * another member means signing in as Natlink again first.
 */
export function isImpersonationAllowedForSession(
  sessionEmail: string | null | undefined
): boolean {
  return import.meta.env.DEV || isNatlinkStaffEmail(sessionEmail);
}

/**
 * Signs the browser session in as the member with the given email (or B2B
 * username). A 404 Result error carries the backend's "no member found"
 * message from ProblemDetails.
 */
export async function impersonateMember(
  email: string
): Promise<Result<ImpersonationViewModel>> {
  const env = getEnv();
  return postJsonWithProblemDetails<ImpersonationViewModel>(
    `${env.apiBaseUrl}/dev/impersonate`,
    { email }
  );
}
